/**
 * Todas as rotas do app em um lugar só.
 *
 * A V1 resolvia rota por comparação de strings com fallback silencioso para a
 * primeira página (`pages/index.tsx:100`) — errar o nome levava ao Dashboard em
 * vez de dar erro. Com constantes, errar o nome não compila.
 *
 * kebab-case minúsculo: link de e-mail de verificação e deep link do mobile
 * ficam legíveis, e `features/dashboard` ↔ `/dashboard` vira regra mecânica.
 */
export const ROTAS = {
  // Públicas
  home: '/',
  login: '/login',
  registro: '/registro',
  esqueciSenha: '/esqueci-senha',
  redefinirSenha: '/redefinir-senha',
  verificarEmail: '/verificar-email',
  /**
   * Entrada vinda do app: troca o token de uso único de
   * `POST /v1/auth/handoff-web` por sessão e segue para o `?next=`.
   *
   * Pública porque quem chega aqui ainda NÃO tem cookie — é justamente o que a
   * página vai buscar. Ver `ROTAS_PUBLICAS` abaixo.
   */
  entrarPeloApp: '/entrar-app',
  termosDeUso: '/termos-de-uso',
  politicaDePrivacidade: '/politica-de-privacidade',

  /**
   * Documento (receituário/requisição) aberto pelo link compartilhado.
   *
   * Pública porque quem abre é o TUTOR, que não tem conta no sistema — o token
   * na URL é a credencial inteira. É este endereço que o backend monta em
   * `compartilhamento.service.ts` (`${APP_WEB_URL}/documento/${token}`); os dois
   * lados precisam continuar iguais.
   *
   * Path do `<Route>`. Para navegar, use `rotaDoDocumentoCompartilhado(token)`.
   */
  documentoCompartilhado: '/documento/:token',

  /**
   * Verificação da receita de controle especial — o QR impresso no PDF.
   *
   * Pública porque quem abre é a FARMÁCIA. O backend monta
   * `${APP_WEB_URL}/verificar/${codigo}` (`urlDeVerificacao`) — os dois lados
   * precisam continuar iguais.
   */
  verificarReceita: '/verificar/:codigo',

  /**
   * Aceite de convite para um ambiente. Pública porque quem abre ainda NÃO tem
   * conta — o token da URL é a credencial inteira, validada pelo backend.
   * O backend monta `${APP_WEB_URL}/convite/${token}` — os dois lados iguais.
   *
   * Path do `<Route>`. Para navegar, use `rotaDeAceitarConvite(token)`.
   */
  aceitarConvite: '/convite/:token',


  // Privadas
  dashboard: '/dashboard',
  perfil: '/perfil',
  /**
   * Para onde o Serpro devolve o médico depois da aprovação no app SerproID.
   *
   * ⚠️ É esta URL — `${APP_WEB_URL}/perfil/serproid`, em HTTPS — que está
   * cadastrada na aplicação do Serpro, e o backend a monta com o mesmo
   * caminho (`CAMINHO_DE_RETORNO_DO_SERPROID`). Mudar um sem o outro faz o
   * Serpro mostrar tela de erro ao médico.
   */
  perfilSerproId: '/perfil/serproid',

  /** Administração do próprio ambiente pelo médico: equipe, convites e cargos. */
  equipe: '/equipe',

  // Clínico
  tutores: '/tutores',
  pacientes: '/pacientes',
  exames: '/exames',
  /** Path do `<Route>`. Para navegar, use `rotaDoExame(id)`. */
  exameDetalhe: '/exames/:id',
  /** Aceita `?novo=1` para já abrir o formulário — veja `rotaDeNovaRequisicao`. */
  requisicoes: '/requisicoes-exames',
  receituarios: '/receituarios',
  /** Path do `<Route>`. Para navegar, use `rotaDoHistorico(id)`. */
  historicoPaciente: '/pacientes/:id/historico',
  /**
   * Ficha clínica: diagnósticos e histórico de peso do paciente.
   *
   * Path do `<Route>`. Para navegar, use `rotaDaFichaClinica(id)`.
   *
   * Separada do histórico de propósito: lá é dossiê de LEITURA sob
   * `historico.visualizar`; aqui se escreve diagnóstico (`diagnosticos.*`) e
   * peso (`pacientes.*`). Aceita `?aba=diagnosticos|peso`.
   */
  fichaClinicaPaciente: '/pacientes/:id/ficha-clinica',
  /**
   * Análise comparativa de grupos de pacientes.
   *
   * Os filtros (grupo, analito, período) vivem na QUERY STRING, e não em estado
   * local como nas outras listas: uma análise é algo que se manda para um
   * colega, e um link que não carrega o grupo selecionado não serve para isso.
   */
  analises: '/analise-comparativa',

  // Discussões técnicas
  discussoes: '/discussoes',
  discussaoNova: '/discussoes/nova',
  /** Path do `<Route>`. Para navegar, use `rotaDaDiscussao(id)`. */
  discussaoDetalhe: '/discussoes/:id',

  /** Lado do advisor: discussões já atribuídas a ele + saldo de vagas. */
  atendimentos: '/atendimentos',

  /** Lado do advisor: artigos que aparecem no rodapé das discussões que ele atende. */
  meusArtigos: '/artigos',

  // Suporte — chamados abertos pelo próprio usuário.
  //
  // ⚠️ Não existe `/suporte/novo`, de propósito: abrir chamado é sempre um
  // `<Dialog>` (do botão flutuante ou do botão da lista). Além de evitar a
  // armadilha de `novo` casar com `:id`, é o que garante que os dois pontos de
  // entrada mostrem exatamente o mesmo formulário.
  suporte: '/suporte',
  /** Path do `<Route>`. Para navegar, use `rotaDoChamado(id)`. */
  suporteDetalhe: '/suporte/:id',

  // Parceiros — vitrine do médico
  parceiros: '/parceiros',
  /** Path do `<Route>`. Para navegar, use `rotaDoParceiro(id)`. */
  parceiroDetalhe: '/parceiros/:id',

  /**
   * Clube Hormonalle — ranque do ambiente e cupons dos parceiros.
   *
   * Fora do catálogo de permissões, como Dashboard e Perfil (PLANO-MVP §4): o
   * recorte é pelo RANQUE, dentro da resposta, não por chave de cargo.
   */
  clube: '/clube',

  // Portal do parceiro (usuário do tenant PARCEIRO)
  portalMateriais: '/portal/materiais',

  // Administração — espelham o prefixo `/v1/admin/*` do backend, para uma URL
  // da tela apontar sem ambiguidade para a rota que a alimenta.
  adminCargos: '/admin/cargos',
  adminUsuarios: '/admin/usuarios',
  adminAmbientes: '/admin/ambientes',
  adminCatalogoExames: '/admin/catalogo-exames',
  /** Fila de nomes de teste que o laboratório mandou e não casaram (B9-5). */
  adminCatalogoPendencias: '/admin/catalogo-exames/pendencias',
  /**
   * Path do `<Route>`. Para navegar, use `rotaDoExameDoCatalogo(id)`.
   *
   * ⚠️ Declarada DEPOIS de `adminCatalogoPendencias` no router: `:id` casaria
   * com "pendencias" e a fila abriria como se fosse um exame.
   */
  adminCatalogoExameDetalhe: '/admin/catalogo-exames/:id',
  adminCatalogoFarmacos: '/admin/catalogo-farmacos',
  /**
   * Path do `<Route>`. Para navegar, use `rotaDoFarmacoDoCatalogo(id)`.
   *
   * O fármaco tem detalhe próprio porque as apresentações comerciais só fazem
   * sentido junto do princípio ativo. O diagnóstico não tem — ele não tem
   * sub-lista nenhuma, e a tela seria um cabeçalho sozinho.
   */
  adminCatalogoFarmacoDetalhe: '/admin/catalogo-farmacos/:id',
  adminCatalogoDiagnosticos: '/admin/catalogo-diagnosticos',
  adminCupons: '/admin/cupons',
  adminUnidades: '/admin/unidades',
  adminParceiros: '/admin/parceiros',
  adminProdutosParceiros: '/admin/produtos-parceiros',
  adminMateriais: '/admin/materiais',
  adminMetricasParceiros: '/admin/metricas/parceiros',
  adminAuditoria: '/admin/auditoria',
  adminRelatorios: '/admin/relatorios',
  adminPrecosDiscussao: '/admin/precos-discussao',
  adminEspecialidadesDiscussao: '/admin/especialidades-discussao',
  adminAdvisors: '/admin/advisors',

  /** Fila de suporte — cross-tenant, chave `admin_suporte.*`. */
  adminSuporte: '/admin/suporte',
  /** Path do `<Route>`. Para navegar, use `rotaDoChamadoAdmin(id)`. */
  adminSuporteDetalhe: '/admin/suporte/:id',

  /** Alertas de erro — chave `admin_alertas.*`. */
  adminAlertas: '/admin/alertas',
  /** Path do `<Route>` — é o link do e-mail de alerta crítico. Para navegar, use `rotaDoAlerta(id)`. */
  adminAlertaDetalhe: '/admin/alertas/:id',

  // Erros
  naoAutorizado: '/403',
  naoEncontrado: '/404',
} as const;

export type Rota = (typeof ROTAS)[keyof typeof ROTAS];

/**
 * URL do exame do catálogo.
 *
 * Existe para o `:id` nunca ser interpolado à mão: `` `/admin/catalogo-exames/${id}` ``
 * espalhado pelas telas é como o caminho e a constante começam a divergir.
 */
export function rotaDoExameDoCatalogo(id: string): string {
  return ROTAS.adminCatalogoExameDetalhe.replace(':id', id);
}

/** URL do fármaco do catálogo. Mesmo motivo do `rotaDoExameDoCatalogo`. */
export function rotaDoFarmacoDoCatalogo(id: string): string {
  return ROTAS.adminCatalogoFarmacoDetalhe.replace(':id', id);
}

/** URL do detalhe do parceiro. Mesmo motivo do `rotaDoExameDoCatalogo`. */
export function rotaDoParceiro(id: string): string {
  return ROTAS.parceiroDetalhe.replace(':id', id);
}

/** URL do laudo do exame. Mesmo motivo do `rotaDoExameDoCatalogo`. */
export function rotaDoExame(id: string): string {
  return ROTAS.exameDetalhe.replace(':id', id);
}

/** URL do detalhe da discussão técnica. Mesmo motivo do `rotaDoExameDoCatalogo`. */
export function rotaDaDiscussao(id: string): string {
  return ROTAS.discussaoDetalhe.replace(':id', id);
}

/** URL do chamado de suporte, do ponto de vista de quem o abriu. */
export function rotaDoChamado(id: string): string {
  return ROTAS.suporteDetalhe.replace(':id', id);
}

/**
 * URL do chamado na tela de ATENDIMENTO.
 *
 * Rota separada da de cima, e não a mesma tela com um `if`: o que o atendente
 * vê (contato do solicitante, botão de assumir, previsão, conclusão) não tem
 * interseção com o que o solicitante vê, e as duas consomem endpoints
 * diferentes.
 */
export function rotaDoChamadoAdmin(id: string): string {
  return ROTAS.adminSuporteDetalhe.replace(':id', id);
}

/** Abre a tela de alertas já com o detalhe deste alerta. */
export function rotaDoAlerta(id: string): string {
  return ROTAS.adminAlertaDetalhe.replace(':id', id);
}

/**
 * URL da página pública do documento compartilhado.
 *
 * O token é `base64url` (gerado com `randomBytes(32).toString('base64url')`),
 * que já é seguro em path — mas o `encodeURIComponent` fica porque quem chama
 * não deveria precisar saber disso.
 */
export function rotaDoDocumentoCompartilhado(token: string): string {
  return ROTAS.documentoCompartilhado.replace(':token', encodeURIComponent(token));
}

/** URL da página pública de aceite do convite. Mesmo motivo do documento. */
export function rotaDeAceitarConvite(token: string): string {
  return ROTAS.aceitarConvite.replace(':token', encodeURIComponent(token));
}

/**
 * Listagem de requisições já com o formulário de criação aberto.
 *
 * Um atalho que promete "criar" e entrega uma listagem obriga o usuário a achar
 * o botão de novo; o `?novo=1` faz a tela abrir o diálogo sozinha. É deep link,
 * e não estado empurrado na navegação, para o mesmo endereço funcionar colado
 * no navegador.
 */
export function rotaDeNovaRequisicao(): string {
  return `${ROTAS.requisicoes}?novo=1`;
}

/**
 * URL do histórico clínico do paciente, opcionalmente já numa aba.
 *
 * A V1 abria o prontuário direto pelo card com
 * `Historico?paciente=X&view=prontuario`; o `?aba=` é o equivalente aqui.
 */
export function rotaDoHistorico(
  pacienteId: string,
  aba?: 'exames' | 'prontuario' | 'linha-do-tempo' | 'evolucao',
): string {
  const caminho = ROTAS.historicoPaciente.replace(':id', pacienteId);
  return aba ? `${caminho}?aba=${aba}` : caminho;
}

/**
 * URL da ficha clínica do paciente, opcionalmente já numa aba.
 *
 * Mesmo motivo do `rotaDoExameDoCatalogo`: `:id` nunca interpolado à mão.
 */
export function rotaDaFichaClinica(pacienteId: string, aba?: 'diagnosticos' | 'peso'): string {
  const caminho = ROTAS.fichaClinicaPaciente.replace(':id', pacienteId);
  return aba ? `${caminho}?aba=${aba}` : caminho;
}

/**
 * Rotas que um visitante SEM sessão pode ver.
 *
 * Existe por causa do 401: nessas telas, `GET /me` respondendo 401 é o estado
 * normal de quem nunca logou, não sessão expirada. Sem esta lista, o
 * AuthProvider mandaria todo visitante da landing para o `/login` — que é
 * exatamente o que a landing existe para evitar.
 */
const ROTAS_PUBLICAS: readonly string[] = [
  ROTAS.home,
  ROTAS.login,
  ROTAS.registro,
  ROTAS.esqueciSenha,
  ROTAS.redefinirSenha,
  ROTAS.verificarEmail,
  ROTAS.entrarPeloApp,
  ROTAS.termosDeUso,
  ROTAS.politicaDePrivacidade,
];

/**
 * Rotas públicas com PARÂMETRO, que a lista acima não consegue expressar.
 *
 * `ROTAS_PUBLICAS` compara string exata, e `/documento/:token` nunca casaria
 * com `/documento/AbC123`. Aqui a comparação é por prefixo — e o prefixo é
 * derivado da própria constante, para o dia em que o caminho mudar não deixar
 * esta lista para trás.
 */
const PREFIXOS_PUBLICOS: readonly string[] = [
  ROTAS.documentoCompartilhado.replace('/:token', ''),
  ROTAS.aceitarConvite.replace('/:token', ''),
  ROTAS.verificarReceita.replace('/:codigo', ''),
];

export function ehRotaPublica(caminho: string): boolean {
  // Normaliza a barra final: `/login/` e `/login` são a mesma rota para o
  // router, e precisam ser a mesma coisa aqui também.
  const normalizado = caminho.length > 1 ? caminho.replace(/\/+$/, '') : caminho;

  if (ROTAS_PUBLICAS.includes(normalizado)) return true;

  // `${prefixo}/` e não `startsWith(prefixo)`: sem a barra, uma rota privada
  // futura chamada `/documentos-internos` entraria como pública por acidente.
  return PREFIXOS_PUBLICOS.some((prefixo) => normalizado.startsWith(`${prefixo}/`));
}

/** Monta `/login?next=<destino>` preservando query e hash do destino. */
export function loginComRetorno(destino: string): string {
  return `${ROTAS.login}?next=${encodeURIComponent(destino)}`;
}

/**
 * Lê o `next` da query. Só aceita caminho relativo — `next=https://malicioso`
 * viraria open redirect, que é a forma clássica de usar a tela de login de um
 * sistema legítimo como trampolim de phishing.
 */
export function destinoSeguro(busca: string, padrao: string = ROTAS.dashboard): string {
  const next = new URLSearchParams(busca).get('next');
  if (!next) return padrao;
  // "//host" e "https://host" são absolutos; só "/caminho" é aceito.
  if (!next.startsWith('/') || next.startsWith('//')) return padrao;
  return next;
}
